import React from 'react';
import { Box, Text, Badge, HStack } from '@chakra-ui/react';
import { ICsvPreviewResponse } from '../../../../endpoints/sessions';

interface PreviewTableProps {
  preview: ICsvPreviewResponse;
}

/**
 * Tabella preview prime righe convertite + conflict indicators (Step 5 wizard).
 */
export const PreviewTable: React.FC<PreviewTableProps> = ({ preview }) => {
  return (
    <Box>
      <HStack gap={3} mb={3} flexWrap="wrap">
        <Badge colorPalette="blue" fontSize="13px" px={3} py={1}>
          Totale righe: {preview.totals.totalRows}
        </Badge>
        <Badge colorPalette="green" fontSize="13px" px={3} py={1}>
          ⊕ Importabili: {preview.totals.importable}
        </Badge>
        {preview.totals.conflicts > 0 && (
          <Badge colorPalette="orange" fontSize="13px" px={3} py={1}>
            ✎ Conflitti: {preview.totals.conflicts}
          </Badge>
        )}
        {preview.totals.skippedNoAthlete > 0 && (
          <Badge colorPalette="red" fontSize="13px" px={3} py={1}>
            ⊘ No atleta: {preview.totals.skippedNoAthlete}
          </Badge>
        )}
        {preview.totals.skippedNoDate > 0 && (
          <Badge colorPalette="red" fontSize="13px" px={3} py={1}>
            ⊘ No data: {preview.totals.skippedNoDate}
          </Badge>
        )}
      </HStack>

      <Box overflowX="auto" maxHeight="500px" overflowY="auto">
        <Box
          as="table"
          width="100%"
          style={{ borderCollapse: 'collapse', fontSize: '12px' }}
        >
          <thead style={{ position: 'sticky', top: 0, background: 'var(--c-navy-1)' }}>
            <tr>
              <th style={{ padding: 8, color: '#fff', textAlign: 'left' }}>
                #
              </th>
              <th style={{ padding: 8, color: '#fff', textAlign: 'left' }}>
                Atleta
              </th>
              <th style={{ padding: 8, color: '#fff', textAlign: 'left' }}>
                Data
              </th>
              <th style={{ padding: 8, color: '#fff', textAlign: 'left' }}>
                Stato
              </th>
              <th style={{ padding: 8, color: '#fff', textAlign: 'left' }}>
                Metriche estratte
              </th>
              <th style={{ padding: 8, color: '#fff', textAlign: 'left' }}>
                Warnings
              </th>
            </tr>
          </thead>
          <tbody>
            {preview.rows.map((row, idx) => {
              const isOk = row.athleteId && row.date && !row.conflict;
              const isConflict = !!row.conflict;
              const isSkipped = !row.athleteId || !row.date;
              const bg = isConflict
                ? '#fef3c7'
                : isSkipped
                ? '#fee2e2'
                : idx % 2 === 0
                ? 'var(--c-row-stripe)'
                : 'var(--c-card-bg)';

              const metrics = row.sessionDraft?.commonMetrics ?? {};
              const internal = row.sessionDraft?.internalLoad ?? {};
              const summary = [
                metrics.td !== undefined ? `TD ${metrics.td}m` : null,
                metrics.hsr !== undefined ? `HSR ${metrics.hsr}m` : null,
                internal.rpe !== undefined ? `RPE ${internal.rpe}` : null,
                internal.srpe !== undefined ? `sRPE ${internal.srpe}` : null,
              ]
                .filter(Boolean)
                .join(' · ');

              return (
                <tr key={idx} style={{ background: bg }}>
                  <td style={{ padding: 6, fontFamily: 'mono' }}>
                    {row.rowIndex + 2}
                  </td>
                  <td style={{ padding: 6 }}>
                    {row.athleteName ?? <Text color="red.500">—</Text>}
                  </td>
                  <td style={{ padding: 6, fontFamily: 'mono' }}>
                    {row.date ?? '—'}
                  </td>
                  <td style={{ padding: 6 }}>
                    {isOk && (
                      <Badge colorPalette="green" size="sm">
                        ⊕ Nuova
                      </Badge>
                    )}
                    {isConflict && (
                      <Badge colorPalette="orange" size="sm">
                        ✎ Conflict
                      </Badge>
                    )}
                    {isSkipped && (
                      <Badge colorPalette="red" size="sm">
                        ⊘ Skip
                      </Badge>
                    )}
                  </td>
                  <td style={{ padding: 6, fontSize: 11 }}>
                    {summary || '—'}
                  </td>
                  <td style={{ padding: 6, fontSize: 11, color: '#dc2626' }}>
                    {row.warnings?.join(', ') ?? ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Box>
      </Box>
    </Box>
  );
};
